<script lang="ts" setup>
import { computed, provide } from 'vue'
import ServiceModelHttp from './service-models/http/ServiceModelHttp.vue'
import ServiceModelHttpPaging from './service-models/http/ServiceModelHttpPaging.vue'
import ServiceModelJavaScript from './service-models/ServiceModelJavaScript.vue'
import ServiceModelExpression from './service-models/ServiceModelExpression.vue'
import ServiceModelSftp from './service-models/ServiceModelSftp.vue'
import ServiceModelSplit from './service-models/ServiceModelSplit.vue'
import ServiceModelBatch from './service-models/ServiceModelBatch.vue'
import ServiceModelGroup from './service-models/ServiceModelGroup.vue'
import ServiceModelMatch from './service-models/ServiceModelMatch.vue'
import ServiceModelAggregate from './service-models/ServiceModelAggregate.vue'
import ServiceModelFlatten from './service-models/ServiceModelFlatten.vue'
import ServiceModelConcat from './service-models/ServiceModelConcat.vue'
import ServiceModelSubWorkflow from './service-models/ServiceModelSubWorkflow.vue'
import ServiceModelNone from './service-models/ServiceModelNone.vue'
import ServiceExample from './service-models/ServiceExample.vue'
import { serviceExample } from './service-models/service-example'
import { EDITED_SERVICE_KEY, type EditedServiceContext } from '../composables/edited-service-context'
import { serviceModelTypes } from '@/types'
import type { Service, IServiceModel } from '@/types'

const props = withDefaults(
  defineProps<{
    modelValue: Service | null
    isDirty?: boolean
    showFooter?: boolean
  }>(),
  {
    isDirty: false,
    showFooter: true,
  }
)

const emit = defineEmits<{
  'update:modelValue': [service: Service]
  save: []
  discard: []
  run: [serviceId: string, serviceData: Service]
  'toggle-component': [serviceId: string, isComponent: boolean]
}>()

const isComponentValue = computed({
  get: () => !!props.modelValue?.isComponent,
  set: (value) => {
    if (props.modelValue) {
      emit('update:modelValue', { ...props.modelValue, isComponent: value })
      emit('toggle-component', props.modelValue.id, value)
    }
  },
})

const serviceModel = computed<IServiceModel | null>({
  get: () => props.modelValue?.serviceModelType || null,
  set: (newServiceModel) => {
    if (props.modelValue && newServiceModel) {
      emit('update:modelValue', {
        ...props.modelValue,
        serviceModelType: newServiceModel,
      })
    }
  },
})

// The model editors under this one read the service through this: Filter when is a property of the service,
// and a fan-in reads the service before it. Assigning to it emits this editor's update.
const editedService = computed<Service | null>({
  get: () => props.modelValue,
  set: (service) => {
    if (service) emit('update:modelValue', service)
  },
})
provide<EditedServiceContext>(EDITED_SERVICE_KEY, editedService)

// A kind with an example opens with its one-sentence description and the example, above the form.
const example = computed(() => (serviceModel.value ? serviceExample(serviceModel.value) : undefined))
const description = computed(
  () => serviceModelTypes.find((option) => option.type === serviceModel.value?.$type)?.description,
)

// Rejoin, Wait and Return read the service before them and have nothing to author, so no form follows.
const NOTHING_TO_AUTHOR = ['rejoin', 'wait', 'return']

const serviceModelComponents = {
  http: ServiceModelHttp,
  httpPaging: ServiceModelHttpPaging,
  webhook: ServiceModelHttp,
  javaScript: ServiceModelJavaScript,
  expression: ServiceModelExpression,
  sftp: ServiceModelSftp,
  split: ServiceModelSplit,
  batch: ServiceModelBatch,
  group: ServiceModelGroup,
  join: ServiceModelMatch,
  filter: ServiceModelMatch,
  aggregate: ServiceModelAggregate,
  flatten: ServiceModelFlatten,
  concat: ServiceModelConcat,
  subWorkflow: ServiceModelSubWorkflow,
  none: ServiceModelNone,
}

const currentServiceComponent = computed(() => {
  const serviceType = serviceModel.value?.$type || 'none'
  if (NOTHING_TO_AUTHOR.includes(serviceType)) return null
  return (
    serviceModelComponents[serviceType as keyof typeof serviceModelComponents] ||
    ServiceModelNone
  )
})

function handleSave() {
  emit('save')
}

function handleDiscard() {
  emit('discard')
}

function handleRun() {
  if (props.modelValue) {
    emit('run', props.modelValue.id, props.modelValue)
  }
}
</script>

<template>
  <div class="service-editor">
    <template v-if="serviceModel">
      <template v-if="example">
        <p class="mb-1">{{ description }}</p>
        <ServiceExample :example="example" />
      </template>
      <component
        v-if="currentServiceComponent"
        :is="currentServiceComponent"
        v-model="serviceModel as any"
      />
    </template>
    <div v-else class="text-center py-5">
      <p class="text-muted">No service configuration available</p>
    </div>

    <!-- Service-level rarities sit below the model's form — set once, rarely revisited. -->
    <div class="form-check form-switch mt-3">
      <input
        :id="`service-is-component-${modelValue?.id ?? 'none'}`"
        class="form-check-input"
        type="checkbox"
        role="switch"
        v-model="isComponentValue"
      />
      <label
        class="form-check-label"
        :for="`service-is-component-${modelValue?.id ?? 'none'}`"
      >
        Available as a Component
        <i
          class="bi bi-info-circle text-secondary ms-1"
          data-bs-toggle="tooltip"
          title="Subscribers can discover and reuse this service from their Components library."
        ></i>
      </label>
    </div>

    <div v-if="showFooter" class="d-flex justify-content-end gap-2 mt-3">
      <button
        type="button"
        class="bb-btn-secondary-cta btn-sm"
        @click="handleRun"
      >
        <i class="bi bi-play me-1"></i>
        Run
      </button>
      <button
        type="button"
        class="bb-btn-cancel"
        :disabled="!isDirty"
        @click="handleDiscard"
      >
        Discard
      </button>
      <button
        type="button"
        class="bb-btn-primary-cta btn-sm"
        :disabled="!isDirty"
        @click="handleSave"
      >
        <i class="bi bi-save me-1"></i>
        Save
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.service-editor {
  display: flex;
  flex-direction: column;
}
</style>
